import { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { userRequest } from '../../utils/requestMethods';
//import { formatter, getPriceForTerm } from '../../utils/currencyUtils';
import { FiMoreHorizontal } from 'react-icons/fi';
import { Header, CustomPagination, TableDropdown, SkeletonTable, ConfirmationModal } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
//import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import * as Icons from '../../data/class';
import { Link, useParams } from 'react-router-dom';
//import { ImSpinner8 } from 'react-icons/im';
import { toast } from 'react-toastify';
import ErrorHandler from '../../utils/errorHandler';
import { MdCheckCircle, MdClose, MdVisibility, MdAdd, MdEdit, MdDelete, MdToggleOn, MdToggleOff } from 'react-icons/md';
import { getEducationLevelLabel, generateEnrollmentFormUrl } from '../../utils/enrollmentUtils';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: 'rgb(248, 250, 252)',
      fontWeight: "bold",
    },
  },
};

const AllEnrollments = ({ history }) => {
  //useKYCStatusRedirect("/classes");
  const { school } = useStateContext();
  const { levelOfEducation } = useParams();
  const schoolTerm = school?.settings?.current_term;
  //console.log(schoolTerm);
  const [submissions, setSubmissions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [submissionsPagination, setSubmissionsPagination] = useState({
    page: 1,
    limit: 10,
    total_documents: 0,
    total_pages: 0
  });
  const [submissionsSearchInput, setSubmissionsSearchInput] = useState("");
  const [submissionsSearch, setSubmissionsSearch] = useState("");
  const [submissionsSortBy, setSubmissionsSortBy] = useState("date");
  const [submissionsOrder, setSubmissionsOrder] = useState("desc");
  const slug = school?.slug;
  const schoolId = school?._id;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [activeTab, setActiveTab] = useState(levelOfEducation ? 'submissions' : 'templates'); // 'templates' or 'submissions'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingTemplateId, setTogglingTemplateId] = useState(null);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };
  //console.log(accessCode);
  //console.log(schoolId);

  useEffect(() => {
    const t = setTimeout(() => {
      setSubmissionsSearch(submissionsSearchInput?.trim() || "");
      // Reset to first page on new search
      setSubmissionsPagination((prev) => ({ ...prev, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [submissionsSearchInput]);

  const fetchSubmissions = async (
    page = submissionsPagination.page,
    limit = submissionsPagination.limit,
    search = submissionsSearch,
    sortBy = submissionsSortBy,
    order = submissionsOrder
  ) => {
    if (schoolId) {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (levelOfEducation) params.set('level_of_education', levelOfEducation);
        if (limit) params.set('limit', String(limit));
        if (page) params.set('page', String(page));
        if (search) params.set('search', search);
        if (sortBy) params.set('sort_by', sortBy);
        if (order) params.set('order', order);

        const url = `/schools/${schoolId}/enrol-submissions?${params.toString()}`;
        const { data } = await userRequest.get(url);

        setSubmissions(data?.submissions || []);
        setSubmissionsPagination({
          page: data?.page || page,
          limit: data?.limit || limit,
          total_documents: data?.total_documents || 0,
          total_pages: data?.total_pages || 0
        });
      } catch (error) {
        setSubmissions([]);
        ErrorHandler.handleError(error, {
          showToast: true,
          logToConsole: true
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchEnrollmentTemplates = async () => {
    if (schoolId) {
      setTemplateLoading(true);
      try {
        const { data } = await userRequest.get(`/schools/${schoolId}/enrol-form-templates`);
        console.log(data);
        setTemplates(data.templates || data || []);
      } catch (error) {
        console.log(error);
        setTemplates([]);
      } finally {
        setTemplateLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchEnrollmentTemplates();
  }, [schoolId]);

  useEffect(() => {
    // Only fetch submissions when we’re on the submissions view
    if (!schoolId) return;
    if (activeTab !== 'submissions') return;
    fetchSubmissions(
      submissionsPagination.page,
      submissionsPagination.limit,
      submissionsSearch,
      submissionsSortBy,
      submissionsOrder
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schoolId,
    levelOfEducation,
    activeTab,
    submissionsPagination.page,
    submissionsPagination.limit,
    submissionsSearch,
    submissionsSortBy,
    submissionsOrder
  ]);
  
  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const getFieldValue = (fields, fieldName) => {
    if (!fields || !Array.isArray(fields)) return '';
    const field = fields.find(f => f.name === fieldName);
    return field ? field.value : '';
  };

  const templateColumns = [
    {
      name: "Education Level",
      selector: row => (
        <span className="font-semibold text-blue-600">
          {getEducationLevelLabel(row.level_of_education)}
        </span>
      )
    },
    {
      name: "Form Title",
      selector: row => <span className="font-semibold">{row.title}</span>
    },
    /*{
      name: "Description",
      selector: row => (
        <span className="text-gray-600 text-sm max-w-xs truncate">
          {row.description}
        </span>
      )
    },*/
    {
      name: "Status",
      selector: row => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
          row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      name: "Created",
      selector: row => (
        <span className="font-semibold">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      )
    },
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View Form Link",
                icon: <MdVisibility className='text-blue-500 text-2xl' />,
                onClick: () => {
                  const formUrl = generateEnrollmentFormUrl(school?.slug, row.level_of_education);
                  navigator.clipboard.writeText(window.location.origin + formUrl);
                  toast.success('Form link copied to clipboard!');
                },
              },
              {
                label: "Edit Template",
                icon: <MdEdit className='text-green-500 text-2xl' />,
                onClick: () => history.push(`/enrollments/update/${row._id}/${row.level_of_education}`),
              },
              {
                label: row.is_active ? "Close Admissions" : "Reopen Admissions",
                icon: row.is_active
                  ? <MdToggleOff className='text-gray-500 text-2xl' />
                  : <MdToggleOn className='text-green-500 text-2xl' />,
                onClick: () => handleToggleTemplateActive(row),
                disabled: togglingTemplateId === row._id,
              },
              {
                label: "View Submissions",
                icon: <MdVisibility className='text-purple-500 text-2xl' />,
                onClick: () => history.push(`/enrollments/submissions/${row.level_of_education}`),
              },
              {
                label: "Delete Template",
                icon: <MdDelete className='text-red-500 text-2xl' />,
                onClick: () => confirmDeleteTemplate(row),
                danger: true,
              }
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
    },
  ];

  const submissionColumns = [
    {
      name: "Parent Name",
      selector: row => {
        const firstName = getFieldValue(row?.fields, 'parent1_first_name');
        const lastName = getFieldValue(row?.fields, 'parent1_last_name');
        return <span className="font-semibold">{`${firstName || ''} ${lastName || ''}`.trim() || 'N/A'}</span>;
      }
    },
    {
      name: "Parent Email",
      selector: row => <span className="font-semibold">{row?.user_email || 'N/A'}</span>
    },
    {
      name: "Student Name",
      width: "220px",
      selector: row => {
        const firstName = getFieldValue(row?.fields, 'child_first_name');
        const lastName = getFieldValue(row?.fields, 'child_last_name');
        const fullName = `${firstName || ''} ${lastName || ''}`.trim() || 'N/A';
        return (
          <div className="flex flex-col gap-1 py-1 min-w-0">
            <span className="font-semibold text-gray-900 leading-tight">{fullName}</span>
            {row?.candidate_number ? (
              <span className="flex items-center gap-1 text-[11px] font-mono font-semibold text-indigo-600 leading-tight">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                {row.candidate_number}
              </span>
            ) : null}
          </div>
        );
      }
    },
    {
      name: "Class",
      selector: row => (
        <span className="font-semibold text-blue-600">
          {row?.class?.class_name || 'N/A'}
        </span>
      )
    },
    /*{
      name: "Education Level",
      selector: row => (
        <span className="font-semibold text-blue-600">
          {row?.level_of_education ? getEducationLevelLabel(row.level_of_education) : 'N/A'}
        </span>
      )
    },*/
    {
      name: "Status",
      selector: row => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
          row.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          row.status === 'approved' ? 'bg-green-100 text-green-800' :
          'bg-red-100 text-red-800'
        }`}>
          {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Unknown'}
        </span>
      )
    },
    {
      name: "Date Submitted",
      selector: row => (
        <span className="font-semibold">
          {row?.createdAt ? new Date(row.createdAt).toLocaleDateString() : 'N/A'}
        </span>
      )
    },
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View Details",
                icon: <MdVisibility className='text-blue-500 text-2xl' />,
                onClick: () => history.push(`/enrollments/details/${row._id}`),
              },
              {
                label: "Approve",
                icon: <MdCheckCircle className='text-green-500 text-2xl' />,
                onClick: () => handleApprove(row._id),
                disabled: row.status === 'approved'
              },
              {
                label: "Reject",
                icon: <MdClose className='text-red-500 text-2xl' />,
                onClick: () => handleReject(row._id),
                disabled: row.status === 'rejected'
              }
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
    },
  ];

  // Update the stats calculations using current page of submissions
  // (Total count comes from `submissionsPagination.total_documents`.)
  const totalSubmissions = submissionsPagination.total_documents || submissions.length;
  const pendingSubmissions = submissions.filter(sub => sub.status === 'pending').length;
  const approvedSubmissions = submissions.filter(sub => sub.status === 'approved').length;
  
  // Template stats
  const totalTemplates = templates.length;
  const activeTemplates = templates.filter(template => template.is_active).length;

  const handleApprove = async (submissionId) => {
    try {
      setLoading(true);
      await userRequest.patch(`/schools/${schoolId}/enrol-submissions/${submissionId}`, {
        status: 'approved'
      });
      toast.success('Enrollment submission approved successfully');
      fetchSubmissions(); // Refresh the list
    } catch (error) {
      /*ErrorHandler.handleError(error, {
        showToast: true,
        logToConsole: true
      });*/
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (submissionId) => {
    try {
      setLoading(true);
      await userRequest.patch(`/schools/${schoolId}/enrol-submissions/${submissionId}`, {
        status: 'rejected'
      });
      toast.success('Enrollment submission rejected successfully');
      fetchSubmissions(); // Refresh the list
    } catch (error) {
      ErrorHandler.handleError(error, {
        showToast: true,
        logToConsole: true
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      setDeleting(true);
      await userRequest.delete(`/schools/${schoolId}/enrol-form-template/${templateToDelete._id}`);
      toast.success('Enrollment template deleted successfully');
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      
      // Refresh the templates list
      fetchEnrollmentTemplates();
    } catch (error) {
      ErrorHandler.handleError(error, {
        showToast: true,
        logToConsole: true
      });
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteTemplate = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const handleToggleTemplateActive = async (template) => {
    const nextIsActive = !template.is_active;
    try {
      setTogglingTemplateId(template._id);
      await userRequest.patch(`/schools/${schoolId}/enrol-form-template/${template._id}`, {
        is_active: nextIsActive
      });
      toast.success(
        nextIsActive
          ? 'Admissions reopened for this class'
          : 'Admissions closed for this class'
      );
      fetchEnrollmentTemplates();
    } catch (error) {
      ErrorHandler.handleError(error, {
        showToast: true,
        logToConsole: true
      });
    } finally {
      setTogglingTemplateId(null);
    }
  };

  return (
    <>
    <div className="px-10 pt-10">
        <div className="flex justify-between items-center mb-4">
          <Header 
            category="Enrollments" 
            title={levelOfEducation ? 
              `Submissions - ${getEducationLevelLabel(levelOfEducation)}` : 
              "Enrollment Management"
            } 
          />
          {!levelOfEducation && (
            <Link to="/enrollments/create">
              <button 
                type="button" 
                className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-[#3F3DA5] hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <MdAdd className="mr-2" size={20} />
                Create New Form
              </button>
            </Link>
          )}
        </div>
        
        {/* Tabs - only show when not viewing specific education level submissions */}
        {!levelOfEducation && (
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'templates'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Form Templates ({totalTemplates})
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'submissions'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Submissions ({totalSubmissions})
            </button>
          </div>
        )}

        {/* Submissions controls */}
        {(levelOfEducation || activeTab === 'submissions') && (
          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
              <input
                value={submissionsSearchInput}
                onChange={(e) => setSubmissionsSearchInput(e.target.value)}
                placeholder="Search by parent, student, email, class…"
                className="w-full max-w-xl rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Sort by</label>
                <select
                  value={submissionsSortBy}
                  onChange={(e) => {
                    setSubmissionsSortBy(e.target.value);
                    setSubmissionsPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="date">Date</option>
                  <option value="amount">Amount</option>
                  <option value="status">Status</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Order</label>
                <select
                  value={submissionsOrder}
                  onChange={(e) => {
                    setSubmissionsOrder(e.target.value);
                    setSubmissionsPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
          </div>
        )}
    </div>
    
    {/* Stats Cards */}
    {levelOfEducation ? (
      // Show only submission stats when viewing specific education level
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2 m-2 md:m-2 mt-5 p-2 md:p-5">
        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Submissions</p>
              <p className="text-2xl font-semibold">{totalSubmissions}</p>
            </div>
            <button type="button" className="bg-[#3371E8] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.userMultiple} alt="Submissions Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Pending Submissions</p>
              <p className="text-2xl font-semibold">{pendingSubmissions}</p>
            </div>
            <button type="button" className="bg-[#41D3BD] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.taskDaily} alt="Pending Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Approved Submissions</p>
              <p className="text-2xl font-semibold">{approvedSubmissions}</p>
            </div>
            <button type="button" className="bg-[#E26E6A] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.taskDaily} alt="Approved Icon" />
            </button>
          </div>
        </div>
      </div>
    ) : activeTab === 'templates' ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2 m-2 md:m-2 mt-5 p-2 md:p-5">
        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Templates</p>
              <p className="text-2xl font-semibold">{totalTemplates}</p>
            </div>
            <button type="button" className="bg-[#3371E8] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.userMultiple} alt="Templates Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Active Templates</p>
              <p className="text-2xl font-semibold">{activeTemplates}</p>
            </div>
            <button type="button" className="bg-[#41D3BD] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.taskDaily} alt="Active Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Submissions</p>
              <p className="text-2xl font-semibold">{totalSubmissions}</p>
            </div>
            <button type="button" className="bg-[#E26E6A] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.taskDaily} alt="Submissions Icon" />
            </button>
          </div>
        </div>
      </div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2 m-2 md:m-2 mt-5 p-2 md:p-5">
        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Total Submissions</p>
              <p className="text-2xl font-semibold">{totalSubmissions}</p>
            </div>
            <button type="button" className="bg-[#3371E8] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.userMultiple} alt="Submissions Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Pending Submissions</p>
              <p className="text-2xl font-semibold">{pendingSubmissions}</p>
            </div>
            <button type="button" className="bg-[#41D3BD] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.taskDaily} alt="Pending Icon" />
            </button>
          </div>
        </div>

        <div className="bg-white border-2 border-gray-200 dark:text-gray-200 dark:bg-secondary-dark-bg h-34 rounded-3xl p-8 pt-9 m-3">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400 text-sm">Approved Submissions</p>
              <p className="text-2xl font-semibold">{approvedSubmissions}</p>
            </div>
            <button type="button" className="bg-[#E26E6A] text-white text-2xl opacity-90 text-white hover:drop-shadow-xl rounded-xl py-4 px-4">
              <img src={Icons.taskDaily} alt="Approved Icon" />
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      <div className="border rounded-3xl">
        <DataTable
          data={levelOfEducation ? submissions : (activeTab === 'templates' ? templates : submissions)}
          columns={levelOfEducation ? submissionColumns : (activeTab === 'templates' ? templateColumns : submissionColumns)}
          getRowId={(row) => row._id}
          pagination
          paginationServer={levelOfEducation || activeTab === 'submissions'}
          paginationTotalRows={
            levelOfEducation || activeTab === 'submissions'
              ? submissionsPagination.total_documents
              : templates.length
          }
          paginationDefaultPage={
            levelOfEducation || activeTab === 'submissions'
              ? submissionsPagination.page
              : 1
          }
          paginationPerPage={
            levelOfEducation || activeTab === 'submissions'
              ? submissionsPagination.limit
              : 10
          }
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangePage={(page) => {
            if (!(levelOfEducation || activeTab === 'submissions')) return;
            setSubmissionsPagination((prev) => ({ ...prev, page }));
            fetchSubmissions(page, submissionsPagination.limit, submissionsSearch, submissionsSortBy, submissionsOrder);
          }}
          onChangeRowsPerPage={(newPerPage, page) => {
            if (!(levelOfEducation || activeTab === 'submissions')) return;
            setSubmissionsPagination((prev) => ({ ...prev, limit: newPerPage, page }));
            fetchSubmissions(page, newPerPage, submissionsSearch, submissionsSortBy, submissionsOrder);
          }}
          customStyles={customStyles}
          progressPending={levelOfEducation ? loading : (activeTab === 'templates' ? templateLoading : loading)}
          progressComponent={<SkeletonTable rows={5} columns={6} />}
          highlightOnHover
          striped
        />
      </div>
    </div>

    {/* Delete Confirmation Modal */}
    <ConfirmationModal
      isVisible={showDeleteModal}
      title="Delete Enrollment Template"
      description={`Are you sure you want to delete the template "${templateToDelete?.title}"? This action cannot be undone and will permanently remove all associated data.`}
      onConfirm={handleDeleteTemplate}
      onCancel={cancelDeleteTemplate}
      deleting={deleting}
    />
    </>
  );
};
export default AllEnrollments;
